"use client";
import { baseUrl, localUrl, WRITING_SERVICE_SUBDOMAIN } from "const";
import NextLink, { LinkProps as NextLinkProps } from "next/link";
import { AllHTMLAttributes, useMemo } from "react";
import { useAppSelector } from "@/store";
import { isWritingServiceSelector } from "@/store/global/globalSlice";

type LinkProps = NextLinkProps &
  AllHTMLAttributes<HTMLAnchorElement> & {
    includeSubdomain?: boolean;
  };

export const Link = ({ href = "", includeSubdomain, ...props }: LinkProps) => {
  const isWritingService = useAppSelector(isWritingServiceSelector);

  const isInternal =
    !href.indexOf("/") ||
    !href.indexOf(`https://${baseUrl}`) ||
    (isWritingService && !href.indexOf(`https://${WRITING_SERVICE_SUBDOMAIN}.${baseUrl}`));

  const actualHref: string = useMemo(() => {
    if (!isInternal) return href;

    if ((!href.indexOf("/") && !isWritingService) || (isWritingService && !includeSubdomain)) {
      return localUrl ? `http://${localUrl}` + href : `https://${baseUrl}` + href;
    }

    if (!href.indexOf("/") && isWritingService && includeSubdomain) {
      return localUrl
        ? `http://${localUrl}` + href
        : `https://${WRITING_SERVICE_SUBDOMAIN}.${baseUrl}` + href;
    }

    return href;
  }, [href, isInternal, isWritingService, includeSubdomain]);

  const isDashboardLink = actualHref.includes(`app.${baseUrl}`);

  if ((isInternal && !isWritingService) || (isInternal && includeSubdomain && isWritingService)) {
    return (
      <NextLink href={actualHref} prefetch={false} passHref legacyBehavior>
        <a {...props} href={actualHref}></a>
      </NextLink>
    );
  } else {
    return (
      // eslint-disable-next-line
      <a
        {...props}
        href={actualHref}
        target={isDashboardLink || isInternal ? undefined : "_blank"}
        rel={isDashboardLink || isInternal ? undefined : "noopener noreferrer nofollow"}
      />
    );
  }
};
